import rootStore from '@vue-storefront/core/store'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import i18n from '@vue-storefront/i18n'
import config from 'config'

function isCanBeReached (step: string) {
  if (
    step === 'register' &&
    !rootStore.getters['user/isLoggedIn']
  ) {
    return true
  } else if (
    step === 'product' &&
    rootStore.getters['user/isLoggedIn']
  ) {
    return true
  } else if (
    step === 'shipping' &&
    rootStore.getters['user/isLoggedIn'] &&
    rootStore.state.selling.stepsValidation.product
  ) {
    return true
  } else if (
    step === 'confirm' &&
    rootStore.getters['user/isLoggedIn'] &&
    rootStore.state.selling.stepsValidation.product &&
    rootStore.state.selling.stepsValidation.shipping
  ) {
    return true
  }
  return false
}

function replaceSpecialCharacters (text, substitute) {
  return [...text].map(char =>
    !/^[A-Za-z0-9]*$/.test(char)
      ? substitute
      : char
  ).join('')
}

function prepareMultiselectValues (arr) {
  return arr.map(item => item.value)
}

function prepareBooleanValue (value) {
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return !!value
}

function prepareData (prop, key, inputType?: string) {
  if (key === 'marketplace_price') {
    return [{
      amount: prop,
      currency: currentStoreView().i18n.currencyCode
    }]
  }
  if (inputType === 'boolean') {
    return prepareBooleanValue(prop)
  }
  return Array.isArray(prop) ? prepareMultiselectValues(prop) : prop
}

function prepareValues (product, langCode, fullAttributesMap) {
  const resultObj = {}
  Object.keys(fullAttributesMap).forEach(key => {
    if (!product[key]) return
    resultObj[key] = [{
      locale: fullAttributesMap[key].localizable ? langCode : null,
      scope: null,
      data: prepareData(product[key], key, fullAttributesMap[key].inputType)
    }]
  })
  return resultObj
}

function prepareIdentifier (product) {
  return `${replaceSpecialCharacters(product.name, '_')}_${new Date().valueOf()}`
}

function getCurrentFamily (categoryCode: string): string {
  const categoryToFamilyMap = config.akeneo.category_ids.category_to_family || {}
  return categoryToFamilyMap[categoryCode]
    ? categoryToFamilyMap[categoryCode]
    : ''
}

function prepareProducts (products, langCode, fullAttributesMap) {
  let result = []
  products.forEach(product => {
    const preparedCategories = prepareMultiselectValues(product.categories)
    result.push({
      identifier: product.identifier,
      categories: preparedCategories,
      family: getCurrentFamily(preparedCategories[0]),
      values: prepareValues(product, langCode, fullAttributesMap)
    })
  })
  return result
}

function sortAttrsWithMap (arr, mapOrder) {
  return arr.sort((leftName, rightName) => {
    const leftNameIndex = mapOrder.indexOf(leftName)
    const rightNameIndex = mapOrder.indexOf(rightName)
    if (leftNameIndex < 0 && rightNameIndex >= 0) return 1
    if (rightNameIndex < 0 && leftNameIndex >= 0) return -1
    if (leftNameIndex < rightNameIndex) return -1
    if (leftNameIndex > rightNameIndex) return 1
    return 0
  })
}

function prepareProductsCountString (productsCount) {
  if (productsCount === null) return `0 ${i18n.t('products')}`
  if (currentStoreView().i18n.defaultLocale === 'pl-PL' && productsCount >= 0) {
    if (productsCount === 1) return `${productsCount} ${i18n.t('produkt')}`
    const lastNumber = productsCount % 10
    if (lastNumber >= 2 && lastNumber <= 4) return `${productsCount} ${i18n.t('produkty')}`
    else return `${productsCount} ${i18n.t('products')}`
  }
  return `${productsCount} ${i18n.t('products')}`
}

function isMarketplaceProductSold (soldDate, productTypeId) {
  if (!soldDate || productTypeId !== 'simple') return false
  const parsedSoldDate = new Date(soldDate.replace(/-/g, '/'))
  return new Date().valueOf() > parsedSoldDate.valueOf()
}

function hasNoStock (item) {
  return Boolean(item && item.stock) && item.stock.is_in_stock === false
}

function isProductSoldOut (product) {
  if (!product) return false
  if (product.type_id === 'configurable') {
    const variants = product.configurable_children
    // if (hasNoStock(product)) return true
    return Array.isArray(variants) && variants.length > 0 && variants.every(hasNoStock)
  }
  if (product.type_id !== 'simple') return false
  if (isMarketplaceProductSold(product.marketplace_sold_at, product.type_id)) return true
  return hasNoStock(product)
}

function getProductSoldLabel (product) {
  if (!isProductSoldOut(product)) return ''
  return isMarketplaceProductSold(product.marketplace_sold_at, product.type_id) ? 'Sold' : 'Sold out'
}

export { isCanBeReached, prepareProducts, prepareProductsCountString, prepareIdentifier, getCurrentFamily, sortAttrsWithMap, isMarketplaceProductSold, isProductSoldOut, getProductSoldLabel }
